// Accordion item for a single section
// Renders header (always visible) + collapsible field list
import { useState, useRef, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { fetchCities } from '@startus/lib';
import { useAccordion } from './StartupProfileForm';
import { FieldAccordionItem } from './FieldAccordionItem';
import { StatusBar } from './StatusBar';
import { useFormMode } from '../context/FormModeContext';
import type { StatusConfig } from './StatusBar';
import type { SectionConfig } from '../types/sections';
import type { FieldConfig, FieldOption } from '../types/fields';

// Renders one field with SectionAccordionItem's own resolvedOptions/onAIRegenerate wiring
// already applied — a custom renderFields layout just arranges the returned nodes, it never
// needs to know about city-option fetching or the other per-section plumbing below.
export type RenderFieldFn = (
  field: FieldConfig,
  index: number,
  overrides?: {
    alwaysExpanded?: boolean;
    renderAsTextarea?: boolean;
    // Opt-in: replaces the default "label, comma-joined text" preview shown in the collapsed
    // row with a custom node (e.g. tag pills) — receives the resolved options (dynamic cities
    // catalog when applicable) alongside the raw field value. Falls back to the default preview
    // when the field has no value, so the "Click to enter" empty state stays consistent.
    renderCollapsedPreview?: (value: unknown, options?: FieldOption[]) => ReactNode;
    // Opt-in: collapsed row as a dashboard tile (label on top, value below) instead of the
    // default label-left/value-right row — view-mode only, the expanded editor is unaffected.
    stackedPreview?: boolean;
    // Opt-in: a multiselect field with only one valid choice renders as a single-select
    // dropdown instead of the toggle-pill row (see FieldAccordionItem).
    singleSelectMultiselect?: boolean;
  }
) => ReactNode;

interface SectionAccordionItemProps {
  section: SectionConfig;
  progress: number;
  statuses: StatusConfig[];
  sectionCounts: Record<string, number>;
  onSave?: () => Promise<void>;
  onDiscard?: () => void;
  onAIRegenerate?: (fieldName: string, currentValue: string) => void;
  // Triggers AI rephrase for all AI-enabled fields in this section at once; returns Promise so component manages its own loading state
  onAISectionRegenerate?: () => Promise<void>;
  canAIRegenerate?: boolean;
  renderHeader?: (section: SectionConfig, progress: number, isExpanded: boolean) => ReactNode;
  // Opt-in overrides for the outer wrapper — default keeps the existing standalone card look
  // (own bg/rounded/border) so admin/hub/onboarding callers that don't pass these are unaffected.
  // A caller that renders many sections inside one shared container (e.g. a single card with
  // divide-y between subsections instead of a grid of separate cards) passes className="" and
  // headerClassName="cursor-pointer hover:bg-white/5 transition-colors" to strip the per-item
  // card chrome without touching the header/content markup.
  className?: string;
  headerClassName?: string;
  // Opt-in override for the fields list body — default (omitted) keeps the existing single-column
  // divide-y stack. Return undefined for a given section to fall back to that default (e.g. a
  // caller that only customizes one section by id/fieldName check).
  renderFields?: (section: SectionConfig, renderField: RenderFieldFn) => ReactNode | undefined;
}

// Default section header - Figma: title + status bar below, chevron right
function DefaultSectionHeader({
  section,
  statuses,
  sectionCounts,
  isExpanded,
}: {
  section: SectionConfig;
  statuses: StatusConfig[];
  sectionCounts: Record<string, number>;
  isExpanded: boolean;
  isSaving: boolean;
  onToggle: () => void;
  onSave?: () => void;
}) {
  return (
    <div className="px-5 pt-4 pb-3">
      {/* Top row: title + chevron */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-base text-white">{section.titleKey}</h3>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Segmented status bar — bar only, title/legend shown in general panel */}
      <StatusBar statuses={statuses} counts={sectionCounts} barOnly />
    </div>
  );
}

export function SectionAccordionItem({
  section,
  progress,
  statuses,
  sectionCounts,
  onSave,
  onDiscard,
  onAIRegenerate,
  onAISectionRegenerate,
  canAIRegenerate,
  renderHeader,
  className,
  headerClassName,
  renderFields,
}: SectionAccordionItemProps) {
  const { t } = useTranslation('common');
  const { expandedSections, toggleSection, isGlobalEditFormMode, isEditingAll } = useAccordion();
  const [isSaving, setIsSaving] = useState(false);
  // Independent loading state for section-level AI — does not bleed into per-field buttons
  const [isSectionGenerating, setIsSectionGenerating] = useState(false);
  const { watch } = useFormContext();
  const { isReadOnly } = useFormMode();
  const containerRef = useRef<HTMLDivElement>(null);

  // True when this section has at least one AI-enabled field
  const hasSectionAIFields = useMemo(
    () => section.fields.some((f) => f.aiEnabled),
    [section.fields]
  );

  const handleSectionAIRegenerate = async () => {
    if (!onAISectionRegenerate) return;
    setIsSectionGenerating(true);
    try {
      await onAISectionRegenerate();
    } finally {
      setIsSectionGenerating(false);
    }
  };

  const isExpanded = expandedSections.has(section.id);

  // Tracks previous isExpanded so scroll only fires on a real false->true transition,
  // not on the default-open first section at mount (and not on StrictMode's dev
  // double-invoke of the mount effect, which a one-shot ref flag wouldn't survive).
  const prevExpandedRef = useRef(isExpanded);

  // Scroll section into view when expanded — deferred so DOM reflects new height first
  useEffect(() => {
    const wasExpanded = prevExpandedRef.current;
    prevExpandedRef.current = isExpanded;
    if (wasExpanded || !isExpanded || !containerRef.current) return;
    const el = containerRef.current;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [isExpanded]);

  // Identify country fields in this section to drive city field options
  const countryFieldNames = useMemo(
    () => section.fields.filter((f) => f.optionsSource === 'countries').map((f) => f.name),
    [section.fields]
  );

  // City field names that need dynamic options
  const cityFieldNames = useMemo(
    () => new Set(section.fields.filter((f) => f.optionsSource === 'cities').map((f) => f.name)),
    [section.fields]
  );

  // Watch all country field values to reactively resolve cities
  const countryValues = watch(countryFieldNames);

  // Serialize codes to a stable string so the effect only fires when the actual selection changes
  const selectedCountryCodes = useMemo(() => {
    const allValues = Array.isArray(countryValues) ? countryValues : [countryValues];
    return (allValues.flat().filter(Boolean) as string[]).sort().join(',');
  }, [countryValues]);

  const [cityOptions, setCityOptions] = useState<FieldOption[]>([]);

  // Fetch city options from BE whenever selected countries change
  useEffect(() => {
    if (countryFieldNames.length === 0 || cityFieldNames.size === 0) return;
    if (!selectedCountryCodes) {
      setCityOptions([]);
      return;
    }

    fetchCities(selectedCountryCodes.split(','))
      .then(setCityOptions)
      .catch(() => setCityOptions([]));
  }, [selectedCountryCodes, countryFieldNames.length, cityFieldNames.size]);

  const handleSave = async () => {
    if (!onSave) return;
    setIsSaving(true);
    try {
      await onSave();
    } catch {
      // Expected on save failure: the caller already showed the error toast and reported it
      // to Sentry, and rethrows only so the form keeps its dirty state. Swallowed here so the
      // click handler doesn't leak an unhandled promise rejection.
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = () => {
    toggleSection(section.id);
  };

  return (
    <div
      ref={containerRef}
      className={`section-accordion-item ${className ?? 'bg-[#1a1a1a] rounded-xl border border-white/5'}`}
    >
      {/* Header - always visible, clickable */}
      <div
        onClick={handleToggle}
        className={
          headerClassName ??
          'cursor-pointer hover:bg-white/5 transition-colors rounded-t-xl overflow-hidden'
        }
      >
        {renderHeader ? (
          renderHeader(section, progress, isExpanded)
        ) : (
          <DefaultSectionHeader
            section={section}
            statuses={statuses}
            sectionCounts={sectionCounts}
            isExpanded={isExpanded}
            isSaving={isSaving}
            onToggle={handleToggle}
            onSave={onSave ? handleSave : undefined}
          />
        )}
      </div>

      {/* Collapsible content - fields.
          Animated via the CSS grid-rows trick (0fr <-> 1fr on a grid track)
          instead of the previous instant mount/unmount, which felt abrupt
          enough that users lost track of what changed on the page (user
          report: wanted a slower/visible expand-collapse animation). Content
          stays mounted so field state survives collapse/expand. */}
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          {/* Section description */}
          {section.descriptionKey && (
            <p className="px-5 py-3 text-sm text-gray-400">{section.descriptionKey}</p>
          )}

          {/* Fields accordion */}
          {(() => {
            const renderField: RenderFieldFn = (field, index, overrides) => (
              <FieldAccordionItem
                key={field.name}
                field={field}
                sectionId={section.id}
                fieldIndex={index}
                onAIRegenerate={onAIRegenerate}
                canAIRegenerate={canAIRegenerate}
                resolvedOptions={cityFieldNames.has(field.name) ? cityOptions : undefined}
                alwaysExpanded={overrides?.alwaysExpanded}
                renderAsTextarea={overrides?.renderAsTextarea}
                renderCollapsedPreview={overrides?.renderCollapsedPreview}
                stackedPreview={overrides?.stackedPreview}
                singleSelectMultiselect={overrides?.singleSelectMultiselect}
              />
            );
            const custom = renderFields?.(section, renderField);
            return (
              custom ?? (
                <div className="fields-accordion divide-y divide-white/10 px-5">
                  {section.fields.map((field, index) => renderField(field, index))}
                </div>
              )
            );
          })()}

          {/* Section footer - Discard + AI section rephrase + Save.
              Same edit-only gate as FieldAccordionItem's reformulate button: on
              globalEditToggle pages isReadOnly is always false, so the section-level
              rephrase button also needs isEditingAll or it leaks into view mode. */}
          {(onDiscard ||
            onSave ||
            (onAISectionRegenerate &&
              hasSectionAIFields &&
              !isReadOnly &&
              (!isGlobalEditFormMode || isEditingAll))) && (
            <div className="flex items-center gap-3 px-5 py-4 border-t border-white/5">
              {onDiscard && (
                <button
                  type="button"
                  onClick={onDiscard}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-[#CACACA] border border-white/10 rounded-lg bg-[#2E2E2E] hover:bg-white/5 transition-colors"
                >
                  {t('common.discard')}
                </button>
              )}

              {/* Section-level AI rephrase — visible when section has AI-enabled fields */}
              {onAISectionRegenerate &&
                hasSectionAIFields &&
                !isReadOnly &&
                (!isGlobalEditFormMode || isEditingAll) && (
                  <button
                    type="button"
                    onClick={handleSectionAIRegenerate}
                    disabled={isSectionGenerating || !canAIRegenerate}
                    className="btn btn-startus-gradient h-auto px-4 py-2.5 gap-1.5 flex-1 min-w-0 overflow-hidden text-sm font-medium rounded-lg"
                  >
                    {isSectionGenerating ? (
                      <>
                        <span className="loading loading-spinner loading-xs" />
                        {t('ai.generating')}
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                        {t('ai.reformulateSection')}
                      </>
                    )}
                  </button>
                )}

              {onSave && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-[#0D0208] bg-[#EAEAEA] hover:bg-[#EAEAEA]/80 disabled:opacity-50 rounded-lg transition-colors"
                >
                  {isSaving ? t('common.saving') : t('common.save')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
